import { Box, Group } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';

import { useScrolledPast } from '../../lib/use-scrolled-past';

/**
 * HeaderShell — sticky-translucent header chrome shared by AppHeader,
 * PublicHeader and HomeHeader. The outer `<Box component="header">` carries
 * the sticky positioning and scroll-aware styling; the inner `<Group>` clamps
 * the row to the 1280-max content band with the standard padding.
 *
 *   variant="standard" (default — AppHeader, PublicHeader): blur stays on,
 *   85% white background, bottom border intensifies from transparent →
 *   brand-1 once the visitor has scrolled past 8px.
 *
 *   variant="home" (HomeHeader): blur drops, background flips opaque white,
 *   a tiny box-shadow fades in past scroll>8 — preserves the per-frame blur
 *   perf workaround documented in CLAUDE.md (the StepGlobe SVG repaint cost
 *   makes a persistent backdrop-filter visibly janky on lower-CPU tiers).
 *   The bottom border is the static brand-1 line.
 */
export function HeaderShell({
  variant = 'standard',
  clampContent = true,
  children,
}: {
  variant?: 'standard' | 'home';
  /**
   * When true (default), the inner row clamps to 1280px and centers — so the
   * header edges align with the 1280-max content band on marketing pages.
   * Pass false for the productivity-app shell where the header sits inside a
   * flex column next to a sticky sidebar — there the chrome (bell + avatar)
   * should hug the right edge of the viewport while the page content below
   * stays clamped at 1280 via PageFrame.
   */
  clampContent?: boolean;
  children: ReactNode;
}) {
  const scrolled = useScrolledPast(8);

  // Cream #FFF6F3 in light, warm Ink in dark (brand-only; not cool charcoal).
  const bgScrolled = 'light-dark(rgba(255, 246, 243, 0.98), var(--mantine-color-dark-7))';
  const bgBlur = 'light-dark(rgba(255, 246, 243, 0.95), rgba(33, 28, 22, 0.9))';

  const style: CSSProperties =
    variant === 'home'
      ? {
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backdropFilter: scrolled ? undefined : 'blur(8px)',
          WebkitBackdropFilter: scrolled ? undefined : 'blur(8px)',
          backgroundColor: scrolled ? bgScrolled : bgBlur,
          borderBottom: 'none',
          boxShadow: scrolled ? '0 2px 16px -4px var(--wwaave-brand-border)' : undefined,
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
        }
      : {
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backdropFilter: scrolled ? undefined : 'blur(8px)',
          WebkitBackdropFilter: scrolled ? undefined : 'blur(8px)',
          backgroundColor: scrolled ? bgScrolled : bgBlur,
          borderBottom: 'none',
          boxShadow: scrolled ? '0 2px 16px -4px var(--wwaave-brand-border)' : undefined,
          transition:
            'background-color 200ms ease, box-shadow 200ms ease',
        };

  return (
    <Box component="header" style={style}>
      <Group
        justify="space-between"
        wrap="nowrap"
        px="lg"
        py={12}
        style={
          clampContent
            ? { maxWidth: 1280, marginInline: 'auto', minHeight: 64 }
            : { minHeight: 64 }
        }
      >
        {children}
      </Group>
      <Box
        style={{
          height: 3,
          background: 'var(--wwaave-gradient)',
        }}
      />
    </Box>
  );
}
